import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'sm_cookie_consent';
const CONSENT_VERSION = 1;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ConsentPreferences {
  essential: boolean;  // always true — required for site function
  functional: boolean; // language, theme, personalization
  analytics: boolean;  // anonymous usage statistics
  marketing: boolean;  // advertising and tracking
}

export interface CookieConsentState extends ConsentPreferences {
  hasResponded: boolean;
  isInitialized: boolean;
  showPreferences: boolean;
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: CookieConsentState = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
  hasResponded: false,
  isInitialized: false,
  showPreferences: false,
};

// ─── localStorage helpers ─────────────────────────────────────────────────────

interface StoredConsent extends ConsentPreferences {
  hasResponded: boolean;
}

function persist(prefs: StoredConsent): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      v: CONSENT_VERSION,
      ...prefs,
      updatedAt: new Date().toISOString(),
    }));
  } catch { /* quota exceeded or private mode */ }
}

function hydrate(): StoredConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.v !== CONSENT_VERSION) return null;
    return {
      essential: true,
      functional: !!parsed.functional,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      hasResponded: !!parsed.hasResponded,
    };
  } catch {
    return null;
  }
}

// ─── Slice ────────────────────────────────────────────────────────────────────

const cookieConsentSlice = createSlice({
  name: 'cookieConsent',
  initialState,
  reducers: {
    /** Called once on client mount — loads saved preferences from localStorage. */
    initializeConsent: (state) => {
      const saved = hydrate();
      if (saved) {
        state.essential = true;
        state.functional = saved.functional;
        state.analytics = saved.analytics;
        state.marketing = saved.marketing;
        state.hasResponded = saved.hasResponded;
      }
      state.isInitialized = true;
    },

    /** Accept all cookie categories. */
    acceptAll: (state) => {
      state.essential = true;
      state.functional = true;
      state.analytics = true;
      state.marketing = true;
      state.hasResponded = true;
      state.showPreferences = false;
      persist({ essential: true, functional: true, analytics: true, marketing: true, hasResponded: true });
    },

    /** Accept essential only — reject non-essential categories. */
    rejectAll: (state) => {
      state.essential = true;
      state.functional = false;
      state.analytics = false;
      state.marketing = false;
      state.hasResponded = true;
      state.showPreferences = false;
      persist({ essential: true, functional: false, analytics: false, marketing: false, hasResponded: true });
    },

    /** Save granular preferences chosen in the preferences modal. */
    savePreferences: (state, action: PayloadAction<Omit<ConsentPreferences, 'essential'>>) => {
      state.functional = action.payload.functional;
      state.analytics = action.payload.analytics;
      state.marketing = action.payload.marketing;
      state.hasResponded = true;
      state.showPreferences = false;
      persist({ essential: true, ...action.payload, hasResponded: true });
    },

    openPreferences: (state) => { state.showPreferences = true; },
    closePreferences: (state) => { state.showPreferences = false; },
  },
});

export const {
  initializeConsent,
  acceptAll,
  rejectAll,
  savePreferences,
  openPreferences,
  closePreferences,
} = cookieConsentSlice.actions;

export default cookieConsentSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectCookieConsent = (state: { cookieConsent: CookieConsentState }) => state.cookieConsent;
export const selectHasResponded = (state: { cookieConsent: CookieConsentState }) => state.cookieConsent.hasResponded;
export const selectShowPreferences = (state: { cookieConsent: CookieConsentState }) => state.cookieConsent.showPreferences;
export const selectIsConsentInitialized = (state: { cookieConsent: CookieConsentState }) => state.cookieConsent.isInitialized;
